import { supabase } from '../lib/supabase';

export interface PolicyAcceptance {
  id?: string;
  user_id: string;
  policy_type: string;
  version?: string;
  accepted_at?: string;
  ip_address?: string;
  user_agent?: string;
}

export interface CookieConsent {
  id?: string;
  user_id?: string;
  session_id?: string;
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
  consented_at?: string;
  ip_address?: string;
}

export type PolicyType =
  | 'terms_of_use'
  | 'privacy_policy'
  | 'cookie_policy'
  | 'gdpr_compliance'
  | 'antifraud_policy';

export const policyService = {
  // Check if user has accepted all required policies
  async checkPolicyAcceptance(userId: string): Promise<boolean> {
    try {
      const requiredPolicies: PolicyType[] = [
        'terms_of_use',
        'privacy_policy',
        'cookie_policy',
        'gdpr_compliance',
        'antifraud_policy'
      ];

      const { data, error } = await supabase
        .from('policy_acceptances')
        .select('policy_type')
        .eq('user_id', userId);

      if (error) {
        console.error('Error checking policy acceptance:', error);
        return false;
      }

      const acceptedPolicies = data?.map((p: any) => p.policy_type) || [];
      return requiredPolicies.every(policy => acceptedPolicies.includes(policy));
    } catch (error) {
      console.error('Error in checkPolicyAcceptance:', error);
      return false;
    }
  },

  // Record policy acceptance
  async acceptPolicies(userId: string, policyTypes: PolicyType[]): Promise<boolean> {
    try {
      const clientIP = await policyService.getClientIP();
      const acceptances = policyTypes.map(policyType => ({
        user_id: userId,
        policy_type: policyType,
        version: '1.0',
        ip_address: clientIP,
        user_agent: navigator.userAgent
      }));

      const { error } = await supabase
        .from('policy_acceptances')
        .insert(acceptances);

      if (error) {
        console.error('Error accepting policies:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in acceptPolicies:', error);
      return false;
    }
  },

  // Get user's policy acceptances
  async getUserPolicyAcceptances(userId: string): Promise<PolicyAcceptance[]> {
    try {
      const { data, error } = await supabase
        .from('policy_acceptances')
        .select('*')
        .eq('user_id', userId)
        .order('accepted_at', { ascending: false });

      if (error) {
        console.error('Error getting policy acceptances:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getUserPolicyAcceptances:', error);
      return [];
    }
  },

  // Cookie consent management
  async saveCookieConsent(consent: CookieConsent): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('cookie_consents')
        .upsert({
          ...consent,
          ip_address: await policyService.getClientIP(),
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error saving cookie consent:', error);
        return false;
      }

      // Store in localStorage for quick access
      localStorage.setItem('cookie_consent', JSON.stringify(consent));
      return true;
    } catch (error) {
      console.error('Error in saveCookieConsent:', error);
      return false;
    }
  },

  // Get cookie consent
  async getCookieConsent(userId?: string, sessionId?: string): Promise<CookieConsent | null> {
    try {
      // First check localStorage
      const localConsent = localStorage.getItem('cookie_consent');
      if (localConsent) {
        return JSON.parse(localConsent);
      }

      // Then check database
      let query = supabase
        .from('cookie_consents')
        .select('*');

      if (userId) {
        query = query.eq('user_id', userId);
      } else if (sessionId) {
        query = query.eq('session_id', sessionId);
      } else {
        return null;
      }

      const { data, error } = await query.single();

      if (error || !data) {
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getCookieConsent:', error);
      return null;
    }
  },

  // Generate session ID for anonymous users
  generateSessionId(): string {
    const existing = localStorage.getItem('session_id');
    if (existing) return existing;

    const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('session_id', sessionId);
    return sessionId;
  },

  // Get client IP (placeholder - in production, get from server)
  async getClientIP(): Promise<string> {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch {
      return 'unknown';
    }
  }
};